import {AuthErrorMap, IgnoredAuthErrors} from "@/constants/errors";
import {isAuthError} from "@/utils/typeGuards";
import {AuthError} from "firebase/auth";
import {memo, useEffect, useState} from "react";

type ErrorLike = Error | AuthError | string | null | undefined;

const getErrorCode = (error: ErrorLike) => {
  if (!error) return "";
  if (isAuthError(error)) return error.code;
  if (typeof error === "string") return "";
  return error.name ?? "";
};

const ErrorBox = ({
  error,
  displayTime = 6000,
  className,
}: {
  error: ErrorLike;
  displayTime?: number;
  className?: string;
}) => {
  const code = getErrorCode(error);
  const isIgnored = !error || IgnoredAuthErrors.includes(code);
  // Only the dismissal is state; the error itself is read straight from the
  // prop. Mirroring it into state would render the previous error for one
  // paint on a direct A-to-B change, and nothing at all on the first paint
  // after an error arrives, because a passive effect runs after that paint.
  const [dismissed, setDismissed] = useState<ErrorLike>(null);

  // Hooks run before any early return. `error` routinely starts null and turns
  // truthy later, so returning early first left the hook count varying between
  // renders — tolerated by React 19, but a rules-of-hooks violation.
  useEffect(() => {
    if (!error || displayTime === 0) return;

    const timeoutId = setTimeout(() => {
      setDismissed(error);
    }, displayTime);

    return () => clearTimeout(timeoutId);
  }, [error, displayTime]);

  const content = error ?? "";

  if (isIgnored || !content || dismissed === error) return null;

  return (
    <div
      className={`text-on-error-container bg-error-container px-g-3 mt-g-1 transition-in-circle-center flex flex-col items-center rounded-md py-3 text-center ${className || ""}`}
    >
      CHYBA:{" "}
      {typeof content === "string"
        ? content
        : AuthErrorMap[code as keyof typeof AuthErrorMap] ||
          content?.message ||
          code ||
          "Neznámá chyba"}
    </div>
  );
};

// render the component only if the error is not null
export default memo(ErrorBox);
